import { useEffect, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { getWeatherForecasts } from './api/weather';
import Loading from './Loading';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

// Color gradient based on snowfall inches
const getSnowfallColor = (inches) => {
  if (!inches || inches === 0) return 'bg-white';
  if (inches < 1) return 'bg-blue-50';
  if (inches < 3) return 'bg-blue-100';
  if (inches < 6) return 'bg-blue-200';
  if (inches < 12) return 'bg-blue-300';
  if (inches < 18) return 'bg-blue-400';
  return 'bg-indigo-500 text-white';
};

// Format date for display
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const day = date.getDate();
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
  return { day, weekday };
};

// Model display names
const MODEL_NAMES = {
  gfs: 'GFS',
  ecmwf: 'ECMWF',
  icon: 'ICON'
};

function WeatherForecastTable({ resorts = null, compact = false }) {
  const [forecasts, setForecasts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchForecasts = async () => {
      setLoading(true);
      try {
        const response = await getWeatherForecasts();
        if (response?.data?.forecasts) {
          setForecasts(response.data.forecasts);
        } else {
          setError('No forecast data available');
        }
      } catch (err) {
        setError('Failed to load forecasts');
        console.error(err);
      }
      setLoading(false);
    };

    fetchForecasts();
  }, []);

  if (loading) return <Loading />;
  if (error) return <p className="text-xs text-gray-500 px-6">{error}</p>;
  if (!forecasts || Object.keys(forecasts).length === 0) {
    return <p className="text-xs text-gray-500 px-6">No weather data available. Check back later.</p>;
  }

  // Filter by resorts if provided
  const resortIds = resorts
    ? resorts.map(r => r._id?.toString()).filter(Boolean)
    : Object.keys(forecasts);

  return (
    <div className={classNames(compact ? 'px-2' : 'px-6', 'overflow-x-auto')}>
      {resortIds.map(resortId => {
        const resortData = forecasts[resortId];
        if (!resortData) return null;

        return (
          <ResortForecast
            key={resortId}
            resortData={resortData}
            compact={compact}
          />
        );
      })}
    </div>
  );
}

function ResortForecast({ resortData, compact }) {
  const [expand, setExpand] = useState(!compact);
  const { resort, models, average } = resortData;

  if (!average || average.length === 0) return null;

  // Calculate totals
  const calculateTotal = (data) => {
    return data.reduce((sum, d) => sum + (d.snowfall_inches || 0), 0);
  };

  // Get dates from average data
  const dates = average.map(d => d.date);

  return (
    <div className="mb-4">
      {/* Resort Header */}
      <div
        className="flex items-center justify-between cursor-pointer py-2"
        onClick={() => setExpand(!expand)}
      >
        <div className="flex items-center space-x-2">
          <span className="font-bold text-sm">{resort?.name || 'Unknown Resort'}</span>
          {resort?.elevation_ft && (
            <span className="text-xs text-gray-500">({resort.elevation_ft.toLocaleString()} ft)</span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-xs text-blue-600 font-bold">
            {calculateTotal(average).toFixed(1)}" total
          </span>
          {expand
            ? <ChevronUpIcon className="h-4 w-4" />
            : <ChevronDownIcon className="h-4 w-4" />
          }
        </div>
      </div>

      {/* Forecast Table */}
      {expand && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left py-1 pr-2 font-medium text-gray-600 sticky left-0 bg-white">Model</th>
                {dates.map((date, i) => {
                  const { day, weekday } = formatDate(date);
                  return (
                    <th key={i} className="px-1 py-1 text-center min-w-[40px]">
                      <div className="text-gray-400 text-[10px]">{weekday}</div>
                      <div className="font-medium">{day}</div>
                    </th>
                  );
                })}
                <th className="px-2 py-1 text-center font-bold">Total</th>
              </tr>
            </thead>
            <tbody>
              {/* Model rows */}
              {Object.entries(MODEL_NAMES).map(([modelKey, modelName]) => {
                const modelData = models[modelKey] || [];
                return (
                  <tr key={modelKey} className="border-b border-gray-100">
                    <td className="py-1 pr-2 text-gray-600 sticky left-0 bg-white">{modelName}</td>
                    {dates.map((date, i) => {
                      const dayData = modelData.find(d =>
                        new Date(d.date).toDateString() === new Date(date).toDateString()
                      );
                      const snowfall = dayData?.snowfall_inches || 0;
                      const highWind = dayData?.wind_speed_mph && dayData.wind_speed_mph > 30;

                      return (
                        <td
                          key={i}
                          className={classNames(
                            getSnowfallColor(snowfall),
                            'px-1 py-1 text-center',
                            highWind ? 'italic' : ''
                          )}
                        >
                          {snowfall > 0 ? snowfall.toFixed(1) + '"' : '-'}
                        </td>
                      );
                    })}
                    <td className="px-2 py-1 text-center font-medium">
                      {calculateTotal(modelData).toFixed(1)}"
                    </td>
                  </tr>
                );
              })}

              {/* Average row */}
              <tr className="border-t-2 border-gray-300 font-bold">
                <td className="py-1 pr-2 sticky left-0 bg-white">Average</td>
                {average.map((dayData, i) => {
                  const snowfall = dayData?.snowfall_inches || 0;
                  const highWind = dayData?.wind_speed_mph && dayData.wind_speed_mph > 30;

                  return (
                    <td
                      key={i}
                      className={classNames(
                        getSnowfallColor(snowfall),
                        'px-1 py-1 text-center',
                        highWind ? 'italic' : ''
                      )}
                    >
                      {snowfall > 0 ? snowfall.toFixed(1) + '"' : '-'}
                    </td>
                  );
                })}
                <td className="px-2 py-1 text-center text-blue-600">
                  {calculateTotal(average).toFixed(1)}"
                </td>
              </tr>
            </tbody>
          </table>

          {/* Legend */}
          <div className="flex items-center space-x-4 mt-2 text-[10px] text-gray-500">
            <span><em>Italic</em> = Wind &gt;30mph</span>
            <span className="flex items-center">
              <span className="w-3 h-3 bg-blue-100 mr-1"></span>1-3"
              <span className="w-3 h-3 bg-blue-300 mx-1"></span>6-12"
              <span className="w-3 h-3 bg-indigo-500 mx-1"></span>18"+
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default WeatherForecastTable;
